import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import {
  Alert,
  Box,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  DisabledByDefault as DisabledByDefaultIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from "@mui/icons-material";
import {
  hideModal,
  hideChildModal,
  showModal,
  showChildModal,
} from "../../data/modal";
import { getTerminals } from "../../data/terminals";
import { getContainerTypes } from "../../data/containertype";
import {
  selectFilteredContainerUploadLoadingTerminals,
  selectFilteredContainerUploadUnloadingTerminals,
  selectFilteredContainerUploadTypes,
  selectedFilteredVoyage,
} from "../../selectors/filterSelector";
import {
  addContainerUploadLoadingTerminalFilterValue,
  addContainerUploadUnloadingTerminalFilterValue,
  addContainerUploadTypeFilterValue,
} from "../../data/filter";
import { getContainerUploads } from "../../data/containerupload";
import { getVoyages } from "../../data/voyage";
import VoyagePicker from "../FormFields/RenderVoyagePicker";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData";
import { AutocompleteField } from "../FormFields/RenderAutocompleteField";
import { useTranslation } from "react-i18next";

interface ContainerRow {
  uid: string;
  weight: number;
  full: string;
  type: string;
  rowId: string;
}

interface Group {
  col0: string;
  col1: string;
  containers: ContainerRow[];
}

interface FormValues {
  groups: Group[];
  loadingTerminals: Record<string, any>;
  unloadingTerminals: Record<string, any>;
  types: any;
  loadingTerminalsVals?: Record<string, any>;
  unloadingTerminalsVals?: Record<string, any>;
  typesVals?: Record<string, any>;
  fileName?: string;
  voyage?: string;
  aliasEntityId?: string;
}

interface Props {
  uploadedContainers: Record<string, any[][]>;
  loadingTerminals: string[];
  unloadingTerminals: string[];
  types: string[];
  file: any;
  hasWarning: any;
  callback?: (result?: any) => void | Promise<void>;
}

type TerminalSelectorKey = "loadingTerminals" | "unloadingTerminals";
type MatchSelectorKey = "loadingTerminals" | "unloadingTerminals" | "types";
type AliasEntityType = "terminal" | "containerType";

const REQUEST_TIMEOUT_MS = 15000;

const SMALL_TEXT_SX = {
  fontSize: "0.78rem",
};

const SMALL_BOLD_TEXT_SX = {
  fontSize: "0.78rem",
  fontWeight: 800,
};

const SMALL_CAPTION_SX = {
  fontSize: "0.68rem",
};

const joinUrl = (...parts: string[]) => {
  return parts
    .filter(Boolean)
    .map((part, index) => {
      if (index === 0) {
        return part.replace(/\/+$/g, "");
      }

      return part.replace(/^\/+/g, "").replace(/\/+$/g, "");
    })
    .join("/");
};

const getApiBaseUrl = () => {
  const rawApiUrl = (process.env.REACT_APP_API_URL || "").trim();

  if (!rawApiUrl) {
    return "/api";
  }

  const normalizedApiUrl = rawApiUrl.replace(/\/+$/g, "");

  if (normalizedApiUrl.endsWith("/api")) {
    return normalizedApiUrl;
  }

  return joinUrl(normalizedApiUrl, "api");
};

const getTerminalCrudItemUrl = (id: string | number) => {
  return joinUrl(getApiBaseUrl(), "terminal-crud/terminals", String(id));
};

const getTerminalPatchUrl = (id: string | number) => {
  return joinUrl(getApiBaseUrl(), "terminals", String(id));
};

const getContainerTypePatchUrl = (id: string | number) => {
  return joinUrl(getApiBaseUrl(), "container_types", String(id));
};

const getSavedEntityApiId = (
  entity: any,
  resourceName: "terminals" | "container_types"
) => {
  if (!entity) {
    return "";
  }

  if (entity["@id"]) {
    return entity["@id"];
  }

  if (entity.id) {
    return `/api/${resourceName}/${entity.id}`;
  }

  return "";
};

const getEntityId = (entity: any) => {
  if (!entity) {
    return "";
  }

  if (entity.id) {
    return String(entity.id);
  }

  if (entity["@id"]) {
    return entity["@id"].toString().split("/").filter(Boolean).pop() || "";
  }

  return "";
};

const normalizeOptions = (value: any): any[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.["hydra:member"])) {
    return value["hydra:member"];
  }

  if (Array.isArray(value?.member)) {
    return value.member;
  }

  return [];
};

const requestJson = async (url: string, options: RequestInit = {}) => {
  const controller = new AbortController();

  const timeout = window.setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        Accept: "application/ld+json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        ...(options.headers ?? {}),
      },
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(responseText || response.statusText || "Request failed");
    }

    return responseText ? JSON.parse(responseText) : null;
  } finally {
    window.clearTimeout(timeout);
  }
};

const addAliasToTerminal = async (terminal: any, aliasValue: string) => {
  const terminalId = getEntityId(terminal);

  if (!terminalId) {
    throw new Error("Missing terminal id.");
  }

  const cleanAlias = aliasValue?.trim();

  if (!cleanAlias) {
    throw new Error("Missing alias value.");
  }

  const currentAliases = Array.isArray(terminal?.aliases)
    ? terminal.aliases
    : [];

  const nextAliases = Array.from(
    new Set([...currentAliases.map(String), cleanAlias])
  );

  try {
    return await requestJson(getTerminalPatchUrl(terminalId), {
      method: "PATCH",
      headers: {
        Accept: "application/ld+json",
        "Content-Type": "application/merge-patch+json",
      },
      body: JSON.stringify({
        aliases: nextAliases,
      }),
    });
  } catch {
    const fallbackPayload = {
      ...terminal,
      aliases: nextAliases,
    };

    delete fallbackPayload.labels;
    delete fallbackPayload["@context"];
    delete fallbackPayload["@type"];

    return requestJson(getTerminalCrudItemUrl(terminalId), {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fallbackPayload),
    });
  }
};

const addAliasToContainerType = async (
  containerType: any,
  aliasValue: string
) => {
  const containerTypeId = getEntityId(containerType);

  if (!containerTypeId) {
    throw new Error("Missing container type id.");
  }

  const cleanAlias = aliasValue?.trim();

  if (!cleanAlias) {
    throw new Error("Missing alias value.");
  }

  const currentAliases = Array.isArray(containerType?.aliases)
    ? containerType.aliases
    : [];

  const nextAliases = Array.from(
    new Set([...currentAliases.map(String), cleanAlias])
  );

  try {
    return await requestJson(getContainerTypePatchUrl(containerTypeId), {
      method: "PATCH",
      headers: {
        Accept: "application/ld+json",
        "Content-Type": "application/merge-patch+json",
      },
      body: JSON.stringify({
        aliases: nextAliases,
      }),
    });
  } catch {
    const fallbackPayload = {
      name: containerType?.name ?? null,
      abbreviation: containerType?.abbreviation ?? null,
      teu: containerType?.teu ?? null,
      height: containerType?.height ?? null,
      width: containerType?.width ?? null,
      emptyWeight: containerType?.emptyWeight ?? null,
      aliases: nextAliases,
    };

    return requestJson(getContainerTypePatchUrl(containerTypeId), {
      method: "PUT",
      headers: {
        Accept: "application/ld+json",
        "Content-Type": "application/ld+json",
      },
      body: JSON.stringify(fallbackPayload),
    });
  }
};

const getTerminalSubtitle = (terminal: any) => {
  return (
    terminal?.city ||
    terminal?.country ||
    terminal?.abbreviation ||
    "Terminal"
  );
};

const getContainerTypeTitle = (containerType: any) => {
  return containerType?.name || containerType?.abbreviation || "—";
};

const getContainerTypeSubtitle = (containerType: any) => {
  if (containerType?.abbreviation) {
    return containerType.abbreviation;
  }

  if (containerType?.teu) {
    return `${containerType.teu} TEU`;
  }

  return "Container type";
};

const ContainerUploadForm: React.FC<Props> = ({
  uploadedContainers,
  loadingTerminals,
  unloadingTerminals,
  types,
  file,
  hasWarning,
  callback,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [manualMatches, setManualMatches] = useState<Record<string, any>>({});
  const [aliasDialog, setAliasDialog] = useState<{
    open: boolean;
    entityType: AliasEntityType | null;
    selectorKey: MatchSelectorKey | null;
    rawValue: string;
    selectorValue: string;
  }>({
    open: false,
    entityType: null,
    selectorKey: null,
    rawValue: "",
    selectorValue: "",
  });
  const [aliasSaving, setAliasSaving] = useState(false);

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts) as any),
    [dispatch]
  );

  const refreshVoyageData = useRefreshVoyageData();

  const { modal, terminals: terminalsState, containerTypes: containerTypesState } =
    useSelector((s: any) => ({
      modal: s.modal,
      terminals: s.terminals.data,
      containerTypes: s.containerTypes.data,
    }));

  const terminals = useMemo(
    () => normalizeOptions(terminalsState),
    [terminalsState]
  );

  const containerTypes = useMemo(
    () => normalizeOptions(containerTypesState),
    [containerTypesState]
  );

  const filteredLoading = useSelector(
    selectFilteredContainerUploadLoadingTerminals
  );

  const filteredUnloading = useSelector(
    selectFilteredContainerUploadUnloadingTerminals
  );

  const filteredTypes = useSelector(selectFilteredContainerUploadTypes);
  const { voyage } = useSelector(selectedFilteredVoyage);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      groups: [],
      loadingTerminals: {},
      unloadingTerminals: {},
      types: {},
      loadingTerminalsVals: {},
      unloadingTerminalsVals: {},
      typesVals: {},
      fileName: undefined,
      voyage: voyage?.["@id"] ?? voyage,
      aliasEntityId: "",
    },
  });

  const selectedAliasEntityId = watch("aliasEntityId");

  const { fields: groupFields } = useFieldArray({
    control,
    name: "groups",
  });

  const refreshTerminalOptions = useCallback(async () => {
    await dispatch(getTerminals() as any);

    dispatch(addContainerUploadLoadingTerminalFilterValue(loadingTerminals));
    dispatch(addContainerUploadUnloadingTerminalFilterValue(unloadingTerminals));
  }, [dispatch, loadingTerminals, unloadingTerminals]);

  const refreshContainerTypeOptions = useCallback(async () => {
    await dispatch(getContainerTypes() as any);

    dispatch(addContainerUploadTypeFilterValue(types));
  }, [dispatch, types]);

  useEffect(() => {
    dispatch(getTerminals() as any);
    dispatch(getContainerTypes() as any);
    dispatch(getVoyages() as any);

    dispatch(addContainerUploadLoadingTerminalFilterValue(loadingTerminals));
    dispatch(addContainerUploadUnloadingTerminalFilterValue(unloadingTerminals));
    dispatch(addContainerUploadTypeFilterValue(types));
  }, [dispatch, loadingTerminals, unloadingTerminals, types]);

  useEffect(() => {
    const groups: Group[] = Object.entries(uploadedContainers).map(
      ([key, rows]) => {
        const [col0, col1] = key.split("_");

        const containers: ContainerRow[] = rows.map((r: any, idx) => ({
          uid: r[2] ?? r.containerNumber ?? "",
          weight: r[3] ?? r.grossWeight ?? r.tareWeight ?? "",
          full:
            r[4] ??
            (Number(r.grossWeight) > Number(r.tareWeight) ? "VOL" : "LEEG"),
          type: r[5] ?? r.type,
          rowId: `${key}_${idx}`,
        }));

        return { col0, col1, containers };
      }
    );

    reset({
      groups,
      loadingTerminals: {},
      unloadingTerminals: {},
      types: {},
      loadingTerminalsVals: {},
      unloadingTerminalsVals: {},
      typesVals: {},
      fileName: file,
      voyage: voyage?.["@id"] ?? voyage,
      aliasEntityId: "",
    });

    if (voyage?.["@id"]) {
      setValue("voyage", voyage["@id"]);
    }
  }, [uploadedContainers, reset, voyage, setValue, file]);

  const findEntry = useCallback(
    (arr: any[], value: string) =>
      arr.find((e) => e.filter === String(value).toLowerCase()),
    []
  );

  useEffect(() => {
    loadingTerminals.forEach((val) => {
      const entry = findEntry(filteredLoading, val);
      const selectorValue = `loadingTerminals.${val}`;

      if (entry?.item?.["@id"]) {
        setValue(`${selectorValue}.id` as any, entry.item["@id"]);
        setValue(`${selectorValue}.matches` as any, true);
      }
    });

    unloadingTerminals.forEach((val) => {
      const entry = findEntry(filteredUnloading, val);
      const selectorValue = `unloadingTerminals.${val}`;

      if (entry?.item?.["@id"]) {
        setValue(`${selectorValue}.id` as any, entry.item["@id"]);
        setValue(`${selectorValue}.matches` as any, true);
      }
    });

    types.forEach((val) => {
      const entry = findEntry(filteredTypes, val);
      const selectorValue = `types.selector${val}`;

      if (entry?.item?.["@id"]) {
        setValue(`${selectorValue}.id` as any, entry.item["@id"]);
        setValue(`${selectorValue}.matches` as any, true);
      }
    });
  }, [
    loadingTerminals,
    unloadingTerminals,
    types,
    filteredLoading,
    filteredUnloading,
    filteredTypes,
    findEntry,
    setValue,
  ]);

  const handleTerminalSavedForSelector = useCallback(
    async (
      selectorKey: TerminalSelectorKey,
      rawValue: string,
      savedTerminal: any
    ) => {
      const savedTerminalId = getSavedEntityApiId(savedTerminal, "terminals");
      const selectorValue = `${selectorKey}.${rawValue}`;

      if (savedTerminalId) {
        setValue(`${selectorValue}.id` as any, savedTerminalId, {
          shouldDirty: true,
          shouldValidate: true,
        });

        setValue(`${selectorValue}.matches` as any, false, {
          shouldDirty: true,
          shouldValidate: false,
        });

        setManualMatches((prev) => ({
          ...prev,
          [selectorValue]: {
            item: savedTerminal,
            matches: [rawValue],
          },
        }));
      }

      await refreshTerminalOptions();
    },
    [refreshTerminalOptions, setValue]
  );

  const handleContainerTypeSavedForSelector = useCallback(
    async (rawValue: string, savedContainerType: any) => {
      const savedContainerTypeId = getSavedEntityApiId(
        savedContainerType,
        "container_types"
      );
      const selectorValue = `types.selector${rawValue}`;

      if (savedContainerTypeId) {
        setValue(`${selectorValue}.id` as any, savedContainerTypeId, {
          shouldDirty: true,
          shouldValidate: true,
        });

        setValue(`${selectorValue}.matches` as any, false, {
          shouldDirty: true,
          shouldValidate: false,
        });

        setManualMatches((prev) => ({
          ...prev,
          [selectorValue]: {
            item: savedContainerType,
            matches: [rawValue],
          },
        }));
      }

      await refreshContainerTypeOptions();
    },
    [refreshContainerTypeOptions, setValue]
  );

  const openAliasDialog = useCallback(
    (
      entityType: AliasEntityType,
      selectorKey: MatchSelectorKey,
      rawValue: string
    ) => {
      const selectorValue =
        selectorKey === "types"
          ? `${selectorKey}.selector${rawValue}`
          : `${selectorKey}.${rawValue}`;

      setValue("aliasEntityId", "", {
        shouldDirty: false,
        shouldValidate: false,
      });

      setAliasDialog({
        open: true,
        entityType,
        selectorKey,
        rawValue,
        selectorValue,
      });
    },
    [setValue]
  );

  const closeAliasDialog = useCallback(() => {
    if (aliasSaving) {
      return;
    }

    setAliasDialog({
      open: false,
      entityType: null,
      selectorKey: null,
      rawValue: "",
      selectorValue: "",
    });

    setValue("aliasEntityId", "", {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [aliasSaving, setValue]);

  const handleSaveAlias = useCallback(async () => {
    if (
      !aliasDialog.entityType ||
      !aliasDialog.selectorKey ||
      !aliasDialog.rawValue
    ) {
      return;
    }

    setAliasSaving(true);

    try {
      if (aliasDialog.entityType === "terminal") {
        const selectedTerminal = terminals.find((terminal: any) => {
          const apiId = getSavedEntityApiId(terminal, "terminals");
          return apiId === selectedAliasEntityId;
        });

        if (!selectedTerminal) {
          setAliasSaving(false);
          return;
        }

        const savedTerminal =
          (await addAliasToTerminal(selectedTerminal, aliasDialog.rawValue)) ||
          selectedTerminal;

        await handleTerminalSavedForSelector(
          aliasDialog.selectorKey as TerminalSelectorKey,
          aliasDialog.rawValue,
          {
            ...selectedTerminal,
            ...savedTerminal,
          }
        );
      }

      if (aliasDialog.entityType === "containerType") {
        const selectedContainerType = containerTypes.find(
          (containerType: any) => {
            const apiId = getSavedEntityApiId(
              containerType,
              "container_types"
            );
            return apiId === selectedAliasEntityId;
          }
        );

        if (!selectedContainerType) {
          setAliasSaving(false);
          return;
        }

        const savedContainerType =
          (await addAliasToContainerType(
            selectedContainerType,
            aliasDialog.rawValue
          )) || selectedContainerType;

        await handleContainerTypeSavedForSelector(aliasDialog.rawValue, {
          ...selectedContainerType,
          ...savedContainerType,
        });
      }

      closeAliasDialog();
    } catch (error: any) {
      console.error("Failed to add alias", error);
      window.alert(error?.message || t("failedToAddAlias"));
    } finally {
      setAliasSaving(false);
    }
  }, [
    aliasDialog,
    selectedAliasEntityId,
    terminals,
    containerTypes,
    handleTerminalSavedForSelector,
    handleContainerTypeSavedForSelector,
    closeAliasDialog,
    t,
  ]);

  const handleRemoveAll = useCallback((ids: string[], remove: boolean) => {
    setRemoved((prev) => {
      const next = new Set(prev);

      ids.forEach((id) => {
        if (!id) {
          return;
        }

        if (remove) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });

      return next;
    });
  }, []);

  const handleRemove = (rowId: string) => {
    setRemoved((existing) => {
      const data = new Set(existing);

      if (data.has(rowId)) {
        data.delete(rowId);
      } else {
        data.add(rowId);
      }

      return data;
    });
  };

  const groupsByCol0: Record<string, Group[]> = {};

  groupFields.forEach((g) => {
    const col = (g as any).col0 as string;
    (groupsByCol0[col] = groupsByCol0[col] || []).push(g as Group);
  });

  const buildTerminalSubmitValues = useCallback(
    (
      rawValues: string[],
      selectorKey: TerminalSelectorKey,
      sourceMatches: any[],
      currentValues: Record<string, any> = {}
    ) => {
      return rawValues.reduce((acc: Record<string, any>, rawValue) => {
        const selectorValue = `${selectorKey}.${rawValue}`;
        const existingValue = currentValues?.[rawValue] ?? {};
        const automaticMatch = findEntry(sourceMatches, rawValue);
        const manualMatch = manualMatches[selectorValue];
        const effectiveMatch = automaticMatch || manualMatch;
        const matchedEntity = effectiveMatch?.item;
        const matchedEntityId =
          existingValue?.id || getSavedEntityApiId(matchedEntity, "terminals");

        acc[rawValue] = {
          ...existingValue,
          id: matchedEntityId,
          matches: Boolean(matchedEntityId),
        };

        return acc;
      }, {});
    },
    [findEntry, manualMatches]
  );

  const buildContainerTypeSubmitValues = useCallback(
    (rawValues: string[], currentValues: Record<string, any> = {}) => {
      return rawValues.reduce((acc: Record<string, any>, rawValue) => {
        const selectorValue = `types.selector${rawValue}`;
        const existingValue =
          currentValues?.[`selector${rawValue}`] ??
          currentValues?.[rawValue] ??
          {};

        const automaticMatch = findEntry(filteredTypes, rawValue);
        const manualMatch = manualMatches[selectorValue];
        const effectiveMatch = automaticMatch || manualMatch;
        const matchedEntity = effectiveMatch?.item;
        const matchedEntityId =
          existingValue?.id ||
          getSavedEntityApiId(matchedEntity, "container_types");

        acc[rawValue] = {
          ...existingValue,
          id: matchedEntityId,
          matches: Boolean(matchedEntityId),
        };

        return acc;
      }, {});
    },
    [filteredTypes, findEntry, manualMatches]
  );

  const validateMatchedValues = useCallback(
    (
      loadingValues: Record<string, any>,
      unloadingValues: Record<string, any>,
      typeValues: Record<string, any>
    ) => {
      const missingLoading = loadingTerminals.filter(
        (terminalCode) => !loadingValues?.[terminalCode]?.id
      );

      const missingUnloading = unloadingTerminals.filter(
        (terminalCode) => !unloadingValues?.[terminalCode]?.id
      );

      const missingTypes = types.filter((typeCode) => !typeValues?.[typeCode]?.id);

      const missingMessages = [
        ...missingLoading.map((value) => `${t("loadingTerminal")}: ${value}`),
        ...missingUnloading.map(
          (value) => `${t("unloadingTerminal")}: ${value}`
        ),
        ...missingTypes.map((value) => `${t("containerType")}: ${value}`),
      ];

      return missingMessages;
    },
    [loadingTerminals, unloadingTerminals, types, t]
  );

  const renderMatchTable = useCallback(
    (
      data: string[],
      titleKey: string,
      codeColumnLabelKey: string,
      matchedColumnLabelKey: string,
      selectorKey: MatchSelectorKey
    ) => {
      if (!data?.length) {
        return (
          <Typography variant="body2" color="text.secondary" sx={SMALL_TEXT_SX}>
            {t("noItemsFound", { item: t(titleKey).toLowerCase() })}
          </Typography>
        );
      }

      return (
        <Box mb={3} sx={{ width: "100%" }}>
          <Typography
            variant="subtitle1"
            fontWeight={900}
            sx={{ mb: 1, fontSize: "0.92rem" }}
          >
            {t(titleKey)} ({data.length})
          </Typography>

          <Table
            size="small"
            sx={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#d9eaff",
                color: "text.primary",
                fontWeight: 900,
                borderBottom: "none",
                py: 0.9,
                fontSize: "0.72rem",
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                py: 1.05,
                borderBottom: "1px solid rgba(15, 23, 42, 0.1)",
                verticalAlign: "middle",
                fontSize: "0.76rem",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>{t("status")}</TableCell>
                <TableCell sx={{ width: "18%" }}>
                  {t(codeColumnLabelKey)}
                </TableCell>
                <TableCell sx={{ width: "30%" }}>
                  {t(matchedColumnLabelKey)}
                </TableCell>
                <TableCell sx={{ width: "14%" }}>{t("status")}</TableCell>
                <TableCell align="center">{t("actions")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((val, idx) => {
                const sourceMatches =
                  selectorKey === "loadingTerminals"
                    ? filteredLoading
                    : selectorKey === "unloadingTerminals"
                    ? filteredUnloading
                    : filteredTypes;

                const entry = findEntry(sourceMatches, val);

                const selectorValue =
                  selectorKey === "types"
                    ? `${selectorKey}.selector${val}`
                    : `${selectorKey}.${val}`;

                const manualMatch = manualMatches[selectorValue];
                const effectiveEntry = entry || manualMatch;
                const matched = Boolean(effectiveEntry?.item);
                const matchedItem = effectiveEntry?.item;

                const isTerminalSelector =
                  selectorKey === "loadingTerminals" ||
                  selectorKey === "unloadingTerminals";
                const isContainerTypeSelector = selectorKey === "types";

                return (
                  <TableRow key={`${selectorKey}-${val}-${idx}`}>
                    <TableCell>
                      {matched ? (
                        <CheckCircleIcon
                          sx={{
                            color: "#4caf50",
                            fontSize: 24,
                          }}
                        />
                      ) : (
                        <WarningAmberOutlinedIcon
                          sx={{
                            color: "#f97316",
                            fontSize: 24,
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography sx={SMALL_BOLD_TEXT_SX}>
                        {val || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {matched ? (
                        <Box>
                          <Typography
                            sx={{
                              ...SMALL_BOLD_TEXT_SX,
                              fontWeight: 900,
                            }}
                          >
                            {isTerminalSelector
                              ? matchedItem?.name || matchedItem?.code || "—"
                              : getContainerTypeTitle(matchedItem)}
                          </Typography>

                          <Typography color="text.secondary" sx={SMALL_TEXT_SX}>
                            {isTerminalSelector
                              ? getTerminalSubtitle(matchedItem)
                              : getContainerTypeSubtitle(matchedItem)}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography
                            sx={{
                              ...SMALL_BOLD_TEXT_SX,
                              fontWeight: 900,
                            }}
                          >
                            —
                          </Typography>
                          <Typography
                            sx={{
                              ...SMALL_TEXT_SX,
                              color: "#ef4444",
                              fontWeight: 800,
                            }}
                          >
                            {t("notRecognised")}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>

                    <TableCell>
                      {matched ? (
                        <Chip
                          size="small"
                          label={t("matched")}
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            backgroundColor: "rgba(76, 175, 80, 0.16)",
                            color: "#2e7d32",
                            fontWeight: 900,
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={t("actionRequired")}
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            backgroundColor: "rgba(249, 115, 22, 0.14)",
                            color: "#f97316",
                            fontWeight: 900,
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {matched ? (
                        <Typography
                          color="text.secondary"
                          fontWeight={900}
                          sx={SMALL_TEXT_SX}
                        >
                          —
                        </Typography>
                      ) : isTerminalSelector ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          justifyContent="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() =>
                              openAliasDialog("terminal", selectorKey, val)
                            }
                            sx={{
                              minWidth: 190,
                              justifyContent: "flex-start",
                              borderColor: "#b875e6",
                              color: "#7e22ce",
                              textTransform: "none",
                              fontWeight: 900,
                              py: 0.65,
                            }}
                          >
                            <Box textAlign="left">
                              <Typography
                                sx={{
                                  fontSize: "0.74rem",
                                  fontWeight: 900,
                                  lineHeight: 1.1,
                                }}
                              >
                                {t("addAsAlias")}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                sx={SMALL_CAPTION_SX}
                              >
                                {t("linkToExistingTerminal")}
                              </Typography>
                            </Box>
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() =>
                              openChildModal({
                                title: t("addTerminal"),
                                description: t("createTerminalDescription"),
                                form: "terminal",
                                formProps: {
                                  mode: "create",
                                  initialTerminal: {
                                    name: val,
                                    abbreviation: val,
                                  },
                                  callback: (savedTerminal: any) =>
                                    handleTerminalSavedForSelector(
                                      selectorKey as TerminalSelectorKey,
                                      val,
                                      savedTerminal
                                    ),
                                },
                              })
                            }
                            sx={{
                              minWidth: 195,
                              justifyContent: "flex-start",
                              borderColor: "#8ab9f1",
                              color: "#1976d2",
                              textTransform: "none",
                              fontWeight: 900,
                              py: 0.65,
                            }}
                          >
                            <Box textAlign="left">
                              <Typography
                                sx={{
                                  fontSize: "0.74rem",
                                  fontWeight: 900,
                                  lineHeight: 1.1,
                                }}
                              >
                                {t("addNewTerminal")}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                sx={SMALL_CAPTION_SX}
                              >
                                {t("createNewTerminal")}
                              </Typography>
                            </Box>
                          </Button>
                        </Stack>
                      ) : isContainerTypeSelector ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          justifyContent="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() =>
                              openAliasDialog("containerType", "types", val)
                            }
                            sx={{
                              minWidth: 190,
                              justifyContent: "flex-start",
                              borderColor: "#b875e6",
                              color: "#7e22ce",
                              textTransform: "none",
                              fontWeight: 900,
                              py: 0.65,
                            }}
                          >
                            <Box textAlign="left">
                              <Typography
                                sx={{
                                  fontSize: "0.74rem",
                                  fontWeight: 900,
                                  lineHeight: 1.1,
                                }}
                              >
                                {t("addAsAlias")}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                sx={SMALL_CAPTION_SX}
                              >
                                {t("linkToExistingContainerType")}
                              </Typography>
                            </Box>
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() =>
                              openChildModal({
                                title: t("addContainerType"),
                                description: t("createContainerTypeDescription"),
                                form: "containerType",
                                formProps: {
                                  mode: "create",
                                  initialContainerType: {
                                    name: val,
                                    abbreviation: val,
                                  },
                                  containerTypeDefaults: {
                                    name: val,
                                    abbreviation: val,
                                  },
                                  callback: (savedContainerType: any) =>
                                    handleContainerTypeSavedForSelector(
                                      val,
                                      savedContainerType
                                    ),
                                },
                              })
                            }
                            sx={{
                              minWidth: 195,
                              justifyContent: "flex-start",
                              borderColor: "#8ab9f1",
                              color: "#1976d2",
                              textTransform: "none",
                              fontWeight: 900,
                              py: 0.65,
                            }}
                          >
                            <Box textAlign="left">
                              <Typography
                                sx={{
                                  fontSize: "0.74rem",
                                  fontWeight: 900,
                                  lineHeight: 1.1,
                                }}
                              >
                                {t("addNewContainerType")}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                sx={SMALL_CAPTION_SX}
                              >
                                {t("createContainerType")}
                              </Typography>
                            </Box>
                          </Button>
                        </Stack>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      );
    },
    [
      t,
      filteredLoading,
      filteredUnloading,
      filteredTypes,
      findEntry,
      manualMatches,
      openAliasDialog,
      openChildModal,
      handleTerminalSavedForSelector,
      handleContainerTypeSavedForSelector,
    ]
  );

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    const filteredGroups = formData.groups
      .map((g) => ({
        ...g,
        containers: g.containers.filter((c) => !removed.has(c.rowId)),
      }))
      .filter((g) => g.containers.length > 0);

    const loadingTerminalValues = buildTerminalSubmitValues(
      loadingTerminals,
      "loadingTerminals",
      filteredLoading,
      formData.loadingTerminals
    );

    const unloadingTerminalValues = buildTerminalSubmitValues(
      unloadingTerminals,
      "unloadingTerminals",
      filteredUnloading,
      formData.unloadingTerminals
    );

    const typeValues = buildContainerTypeSubmitValues(types, formData.types);

    const missingValues = validateMatchedValues(
      loadingTerminalValues,
      unloadingTerminalValues,
      typeValues
    );

    if (missingValues.length > 0) {
      window.alert(
        `${t("notAllItemsAreMatched") || "Not all items are matched"}:\n\n${missingValues.join(
          "\n"
        )}`
      );

      return;
    }

    const payload: FormValues & { groups: typeof filteredGroups } = {
      ...formData,
      groups: filteredGroups,
      fileName: file,
      loadingTerminals: loadingTerminalValues,
      unloadingTerminals: unloadingTerminalValues,
      types: typeValues,
    };

    delete (payload as any).aliasEntityId;
    delete (payload as any).loadingTerminalsVals;
    delete (payload as any).unloadingTerminalsVals;
    delete (payload as any).typesVals;

    try {
      const resp = await fetch("/api/container-upload/file", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const responseText = await resp.text();
        throw new Error(responseText || `Server error: ${resp.statusText}`);
      }

      const { success, containerIds, containerUploadId, voyage } = await resp.json();

      if (!success) {
        throw new Error("Server returned success = false");
      }

      dispatch(
        showModal({
          title: t("containerTerminalCall"),
          description: t("linkContainersToTerminalCalls"),
          form: "containerterminalcallform",
          formProps: {
            containerIds,
            containerUploadId,
            voyage,
            hideCloseButton: true,
            disableBackdropClose: true,
            callback: async (result?: any) => {
              await dispatch(getContainerUploads() as any);

              if (callback) {
                await callback({
                  ...(result || {}),
                  voyage,
                  containerIds,
                  containerUploadId,
                });
              }
            },
          },
          size: "wide",
        }) as any
      );

      refreshVoyageData(voyage);
    } catch (e: any) {
      console.error("Save failed", e);
      alert(`${t("saveFailed")}: ${e.message}`);
    } finally {
      dispatch(getContainerUploads() as any);
    }
  };

  const close = () =>
    modal.childModal
      ? dispatch(hideChildModal() as any)
      : dispatch(hideModal() as any);

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        p={2}
        bgcolor="background.paper"
        sx={{
          width: "100%",
          maxWidth: "none",
          boxSizing: "border-box",
        }}
      >
        {hasWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("duplicatedUploadFile")}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "none",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              flex: "0 0 32%",
              mb: 2,
              maxHeight: "80vh",
              minWidth: 340,
              overflow: "auto",
              pr: 2,
              borderRight: "1px solid #ddd",
              "&::-webkit-scrollbar": { width: 0 },
              scrollbarWidth: "none",
              "-ms-overflow-style": "none",
            }}
          >
            {Object.entries(groupsByCol0).map(([col0, groupList]) => {
              const allRowIds = groupList.flatMap((g) =>
                g.containers.map((c) => c.rowId)
              );

              const allRemoved =
                allRowIds.length > 0 &&
                allRowIds.every((id) => removed.has(id));

              return (
                <Box key={col0} mb={3}>
                  <Box
                    p={1}
                    bgcolor="#C1D8F2"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 800 }}>
                      {t("group")} {col0}
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      color={allRemoved ? "success" : "error"}
                      onClick={() => handleRemoveAll(allRowIds, !allRemoved)}
                      sx={{ fontSize: "0.7rem", fontWeight: 800 }}
                    >
                      {allRemoved ? t("restoreAll") : t("removeAll")}
                    </Button>
                  </Box>

                  {groupList.map(({ col1, containers }, idx) => {
                    const groupRowIds = containers.map((c) => c.rowId);

                    const groupAllRemoved =
                      groupRowIds.length > 0 &&
                      groupRowIds.every((id) => removed.has(id));

                    return (
                      <Accordion
                        key={`${col0}-${col1}-${idx}`}
                        sx={{ padding: 0 }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            padding: 0,
                            minHeight: 38,
                            "& .MuiAccordionSummary-content": {
                              my: 0.5,
                            },
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <Typography
                              display="flex"
                              alignItems="center"
                              gap={1}
                              sx={SMALL_BOLD_TEXT_SX}
                            >
                              {col0}
                              <ArrowRightAltIcon
                                fontSize="small"
                                color="primary"
                              />
                              {col1} (
                              {
                                containers.filter(
                                  (c) => !removed.has(c.rowId)
                                ).length
                              }
                              )
                            </Typography>

                            <Button
                              size="small"
                              variant="text"
                              color={groupAllRemoved ? "success" : "error"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAll(groupRowIds, !groupAllRemoved);
                              }}
                              sx={{ fontSize: "0.7rem", fontWeight: 800 }}
                            >
                              {groupAllRemoved
                                ? t("restoreGroup")
                                : t("removeGroup")}
                            </Button>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            padding: 0,
                            width: "100%",
                          }}
                        >
                          <Table
                            size="small"
                            sx={{
                              "& .MuiTableCell-root": {
                                fontSize: "0.72rem",
                                py: 0.6,
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>{t("uid")}</TableCell>
                                <TableCell align="center">
                                  {t("weight")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("full")}
                                </TableCell>
                                <TableCell>{t("type")}</TableCell>
                                <TableCell />
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {containers.map((c, i) => {
                                const isRemoved = removed.has(c.rowId);

                                return (
                                  <TableRow
                                    key={c.rowId ?? i}
                                    sx={{
                                      textDecoration: isRemoved
                                        ? "line-through"
                                        : "none",
                                      opacity: isRemoved ? 0.5 : 1,
                                    }}
                                  >
                                    <TableCell>{c.uid || "N/A"}</TableCell>

                                    <TableCell align="center">
                                      {c.weight}
                                    </TableCell>

                                    <TableCell align="center">
                                      {c.full && c.full === "VOL" ? (
                                        <CheckCircleIcon
                                          fontSize="small"
                                          color="success"
                                        />
                                      ) : (
                                        <DisabledByDefaultIcon
                                          fontSize="small"
                                          color="info"
                                        />
                                      )}
                                    </TableCell>

                                    <TableCell>{c.type}</TableCell>

                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemove(c.rowId)}
                                      >
                                        <CloseIcon
                                          fontSize="small"
                                          color="error"
                                        />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              flex: "1 1 auto",
              ml: 2,
              mb: 2,
              height: "80vh",
              minWidth: 0,
              width: "100%",
              overflow: "auto",
              "&::-webkit-scrollbar": { width: 0 },
              scrollbarWidth: "none",
              "-ms-overflow-style": "none",
            }}
          >
            <Box mb={4}>
              <VoyagePicker control={control} />
            </Box>

            {renderMatchTable(
              loadingTerminals,
              "loadingTerminalsTitle",
              "terminalCodeInFile",
              "matchedTerminal",
              "loadingTerminals"
            )}

            {renderMatchTable(
              unloadingTerminals,
              "unloadingTerminalsTitle",
              "terminalCodeInFile",
              "matchedTerminal",
              "unloadingTerminals"
            )}

            {renderMatchTable(
              types,
              "containerTypesTitle",
              "containerCodeInFile",
              "matchedContainerType",
              "types"
            )}
          </Box>
        </Box>

        <Grid container justifyContent="space-between" alignItems="center">
          <Button
            onClick={close}
            disabled={isSubmitting}
            variant="contained"
            startIcon={<CloseIcon />}
            sx={{ fontSize: "0.78rem", fontWeight: 800 }}
          >
            {t("close")}
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            sx={{ fontSize: "0.78rem", fontWeight: 800 }}
          >
            {t("import")}
          </Button>
        </Grid>
      </Box>

      <Dialog
        open={aliasDialog.open}
        onClose={closeAliasDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>
          {t("addAlias")}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: "0.78rem" }}>
              {t("linkAliasToExisting", {
                value: aliasDialog.rawValue,
                item:
                  aliasDialog.entityType === "containerType"
                    ? t("containerType").toLowerCase()
                    : t("terminal").toLowerCase(),
              })}
            </Alert>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <AutocompleteField
                name="aliasEntityId"
                label={
                  aliasDialog.entityType === "containerType"
                    ? t("containerType")
                    : t("terminal")
                }
                control={control}
                labelIdentifier={
                  aliasDialog.entityType === "containerType"
                    ? ["name", "abbreviation"]
                    : ["name"]
                }
                options={
                  aliasDialog.entityType === "containerType"
                    ? containerTypes
                    : terminals
                }
                required
              />
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeAliasDialog}
            disabled={aliasSaving}
            sx={{ fontSize: "0.78rem", fontWeight: 800 }}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveAlias}
            disabled={aliasSaving || !selectedAliasEntityId}
            sx={{ fontSize: "0.78rem", fontWeight: 800 }}
          >
            {aliasSaving ? t("saving") : t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContainerUploadForm;